import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowUpTrayIcon, 
  DocumentTextIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ExclamationTriangleIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';
import { createPlayerScore } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import CsvScoreImportGuide from './CsvScoreImportGuide';

interface CsvScoreImportProps {
  tournamentId: string;
  onSuccess: () => void;
}

interface ScoreRecord {
  player_id: string;
  round: number;
  score: number;
  date: string;
  notes?: string;
  status: 'pending' | 'success' | 'error';
  error?: string;
}

export default function CsvScoreImport({ tournamentId, onSuccess }: CsvScoreImportProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [records, setRecords] = useState<ScoreRecord[]>([]);
  const [importStats, setImportStats] = useState({
    total: 0,
    success: 0,
    error: 0,
    pending: 0
  });
  const [showGuide, setShowGuide] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file: File) => {
    // Check if file is CSV
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setFile(file);
    parseCSV(file);
  };

  const parseCSV = (file: File) => {
    setIsProcessing(true);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n');
        
        // Extract header row and validate required columns
        const headers = lines[0].split(',').map(header => header.trim().toLowerCase());
        
        const requiredColumns = ['player_id', 'round', 'score', 'date'];
        const missingColumns = requiredColumns.filter(col => !headers.includes(col));
        
        if (missingColumns.length > 0) {
          toast.error(`CSV is missing required columns: ${missingColumns.join(', ')}`);
          setIsProcessing(false);
          return;
        }
        
        // Parse data rows
        const parsedRecords: ScoreRecord[] = [];
        
        for (let i = 1; i < lines.length; i++) {
          if (!lines[i].trim()) continue; // Skip empty lines
          
          const values = lines[i].split(',').map(val => val.trim());
          if (values.length !== headers.length) {
            console.warn(`Line ${i} has incorrect number of values, skipping`);
            continue;
          }
          
          // Create record object from headers and values
          const record: any = {};
          headers.forEach((header, index) => {
            record[header] = values[index];
          });
          
          // Validate and convert data types
          try {
            const scoreRecord: ScoreRecord = {
              player_id: record.player_id,
              round: parseInt(record.round),
              score: parseInt(record.score),
              date: record.date,
              notes: record.notes || '',
              status: 'pending'
            };
            
            // Basic validation
            if (!scoreRecord.player_id || isNaN(scoreRecord.round) || isNaN(scoreRecord.score) || !scoreRecord.date) {
              scoreRecord.status = 'error';
              scoreRecord.error = 'Invalid data format';
            }
            
            parsedRecords.push(scoreRecord);
          } catch (err) {
            console.error(`Error parsing line ${i}:`, err);
          }
        }
        
        setRecords(parsedRecords);
        setImportStats({
          total: parsedRecords.length,
          success: 0,
          error: parsedRecords.filter(r => r.status === 'error').length,
          pending: parsedRecords.filter(r => r.status === 'pending').length
        });
        
        if (parsedRecords.length > 0 && parsedRecords.some(r => r.status === 'pending')) {
          toast.success(`CSV parsed successfully. ${parsedRecords.length} records found.`);
        } else {
          toast.error('No valid records found in CSV');
        }
      } catch (err) {
        console.error('Error parsing CSV:', err);
        toast.error('Failed to parse CSV file');
      } finally {
        setIsProcessing(false);
      }
    };
    
    reader.onerror = () => {
      toast.error('Error reading file');
      setIsProcessing(false);
    };
    
    reader.readAsText(file);
  };

  const importScores = async () => {
    if (records.length === 0) return;
    
    setIsProcessing(true);
    const pendingRecords = records.filter(r => r.status === 'pending');
    
    if (pendingRecords.length === 0) {
      toast.error('No valid records to import');
      setIsProcessing(false);
      return;
    }
    
    let successCount = 0;
    let errorCount = 0;
    
    // Create a copy of records to update
    const updatedRecords = [...records];
    
    // Process each record
    for (let i = 0; i < pendingRecords.length; i++) {
      const record = pendingRecords[i];
      const recordIndex = records.findIndex(r => 
        r.player_id === record.player_id && 
        r.round === record.round
      );
      
      try {
        const { error } = await createPlayerScore({
          player_id: record.player_id,
          tournament_id: tournamentId,
          round: record.round,
          score: record.score,
          date: record.date,
          notes: record.notes
        });
        
        if (error) throw error;
        
        // Update record status
        if (recordIndex !== -1) {
          updatedRecords[recordIndex].status = 'success';
        }
        
        successCount++;
      } catch (err: any) {
        console.error(`Error importing score for player ${record.player_id}:`, err);
        
        // Update record status
        if (recordIndex !== -1) {
          updatedRecords[recordIndex].status = 'error';
          updatedRecords[recordIndex].error = err.message || 'Import failed';
        }
        
        errorCount++;
      }
      
      // Update records state periodically to show progress
      if (i % 5 === 0 || i === pendingRecords.length - 1) {
        setRecords(updatedRecords);
        setImportStats({
          total: records.length,
          success: successCount,
          error: errorCount + records.filter(r => r.status === 'error').length,
          pending: records.length - successCount - errorCount - records.filter(r => r.status === 'error').length
        });
      }
    }
    
    // Final update
    setRecords(updatedRecords);
    setImportStats({
      total: records.length,
      success: successCount,
      error: errorCount + records.filter(r => r.status === 'error').length,
      pending: 0
    });
    
    setIsProcessing(false);
    
    if (successCount > 0) {
      toast.success(`Successfully imported ${successCount} scores`);
      onSuccess();
    }
    
    if (errorCount > 0) {
      toast.error(`Failed to import ${errorCount} scores`);
    }
  };

  const resetImport = () => {
    setFile(null);
    setRecords([]);
    setImportStats({
      total: 0,
      success: 0,
      error: 0,
      pending: 0
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const headers = ['player_id', 'round', 'score', 'date', 'notes'];
    const csvContent = headers.join(',');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', 'score_import_template.csv');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
    >
      <h2 className="text-xl font-semibold text-golf-gray mb-6">Import Scores from CSV</h2>
      
      {!file ? (
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center ${
            isDragging ? 'border-golf-orange bg-golf-orange/5' : 'border-gray-300 hover:border-golf-orange'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <ArrowUpTrayIcon className="w-12 h-12 mx-auto text-gray-400" />
          <p className="mt-4 text-lg font-medium text-gray-700">
            Drag and drop your CSV file here
          </p>
          <p className="mt-2 text-sm text-gray-500">
            or
          </p>
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv"
            onChange={handleFileInput}
            className="hidden"
          />
          <div className="flex justify-center gap-3 mt-4">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-4 py-2 bg-golf-orange text-white rounded-lg hover:bg-golf-orange/90 transition-colors"
            >
              Select CSV File
            </button>
            <button
              onClick={() => setShowGuide(true)}
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <QuestionMarkCircleIcon className="w-5 h-5" />
              <span>Import Guide</span>
            </button>
          </div>
          
          <div className="mt-8 border-t border-gray-200 pt-6">
            <h3 className="text-sm font-medium text-gray-700 mb-2">CSV Format Requirements:</h3>
            <ul className="text-sm text-gray-600 text-left list-disc pl-5 space-y-1">
              <li>File must be in CSV format</li>
              <li>Required columns: player_id, round, score, date</li>
              <li>Optional columns: notes</li>
              <li>player_id must be a valid UUID from the players table</li>
              <li>round must be a positive integer</li>
              <li>score is relative to par (e.g., -2, 0, +3)</li>
              <li>date must be in YYYY-MM-DD format</li>
            </ul>
            <div className="mt-4 flex justify-center">
              <button
                onClick={downloadTemplate}
                className="text-golf-orange hover:text-golf-green flex items-center gap-2"
              >
                <DocumentTextIcon className="w-5 h-5" />
                <span>Download Template</span>
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center gap-3">
              <DocumentTextIcon className="w-8 h-8 text-golf-orange" />
              <div>
                <p className="font-medium text-golf-gray">{file.name}</p>
                <p className="text-sm text-gray-500">{file.size} bytes</p>
              </div>
            </div>
            <button
              onClick={resetImport}
              className="text-gray-500 hover:text-red-500"
            >
              <XCircleIcon className="w-6 h-6" />
            </button>
          </div>
          
          {/* Import Stats */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-sm text-gray-500">Total Records</p>
              <p className="text-2xl font-bold text-golf-gray">{importStats.total}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <p className="text-sm text-green-600">Successful</p>
              <p className="text-2xl font-bold text-green-600">{importStats.success}</p>
            </div>
            <div className="bg-red-50 rounded-lg p-4 text-center">
              <p className="text-sm text-red-600">Failed</p>
              <p className="text-2xl font-bold text-red-600">{importStats.error}</p>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4 text-center">
              <p className="text-sm text-yellow-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{importStats.pending}</p>
            </div>
          </div>
          
          {/* Records Preview */}
          {records.length > 0 && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Player ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Round</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {records.slice(0, 10).map((record, index) => (
                      <tr key={index} className={record.status === 'error' ? 'bg-red-50' : ''}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {record.player_id.substring(0, 8)}...
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {record.round}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {record.score}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {record.date}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {record.status === 'success' ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircleIcon className="w-4 h-4 mr-1" />
                              Success
                            </span>
                          ) : record.status === 'error' ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" title={record.error}>
                              <XCircleIcon className="w-4 h-4 mr-1" />
                              Error
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              Pending
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {records.length > 10 && (
                <div className="px-6 py-3 bg-gray-50 text-sm text-gray-500">
                  Showing 10 of {records.length} records
                </div>
              )}
            </div>
          )}
          
          {/* Import Button */}
          <div className="flex justify-end gap-3">
            <button
              onClick={resetImport}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={importScores}
              disabled={isProcessing || importStats.pending === 0}
              className="px-4 py-2 bg-golf-orange text-white rounded-lg hover:bg-golf-orange/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <ArrowUpTrayIcon className="w-5 h-5" />
                  <span>Import Scores</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
      
      {/* Import Guide Modal */}
      <CsvScoreImportGuide 
        isOpen={showGuide}
        onClose={() => setShowGuide(false)}
      />
    </motion.div>
  );
}